import {
  AnswerTypesMap,
  CreateNewQuestionPayload,
  CreateNewQuestionResponse,
  DeleteQuestionResponse,
  EligibilityCompletePriority,
  EligibilityCompleteRequest,
  EligibilityCompleteRequestStatus,
  FieldIdentifierPayload,
  GetEligibilityRequestsPayload,
  GetRequestsByEligibilityIdResponse,
  ProviderNetworkType,
  Question,
  QuestionField,
  QuestionFieldSubjectEnum,
  QuestionFieldType,
  QuestionModal,
  QuestionTypeEnum,
  UpdateQuestionPayload,
  UpdateQuestionResponse,
  UpdatedRequestResponse,
  customQuestionId
} from '@app/core/models/eligibility-complete.model';
import { HttpMethod, HttpService } from '../http/http.service';

import { APIConstants } from '@app/core/constants/api.constants';
import { AppConstants } from '@app/core/constants/app.constants';
import { AuthService } from '../auth/auth.service';
import { BehaviorSubject } from 'rxjs';
import { DxcToastService } from '@dxc/ui-components/toast';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageConstants } from '@app/core/constants/message.constants';

const INITIAL_STATE = {
  isOpen: false,
  isLoading: false,
  isCustomQuestion: false,
  customQuestion: { question: '', answerType: '' }
};

const fieldTypeToThrowError = [
  QuestionFieldType.Maximums,
  QuestionFieldType.Deductibles,
  QuestionFieldType.Benefits
];

@Injectable({
  providedIn: 'root'
})
export class EligibilityCompleteService {
  isEditorActive = false;
  private _isEditorActiveSource = new BehaviorSubject<boolean>(this.isEditorActive);
  isEditorActive$ = this._isEditorActiveSource.asObservable();

  redirectToCompleteTab = false;
  _redirectToCompleteTab = new BehaviorSubject<boolean>(this.redirectToCompleteTab);
  redirectToCompleteTab$ = this._redirectToCompleteTab.asObservable();

  requestId = -1;
  questionModal: QuestionModal = INITIAL_STATE;

  constructor(
    private _http: HttpService,
    private dxcToastService: DxcToastService,
    private authService: AuthService
  ) {}

  openEditor(requestId: number) {
    this.isEditorActive = true;
    this.requestId = requestId;
  }

  closeEditor() {
    this.isEditorActive = false;
    this.requestId = -1;
    this._isEditorActiveSource.next(false);
  }

  isQuestionAlreadyCreated(fieldIdentifier: QuestionField) {
    const alreadyCreatedQuestion = this.questionModal.allAddedQuestions?.find((question) => {
      if (
        fieldIdentifier.category == question.questionField.category &&
        fieldIdentifier.code == question.questionField.code &&
        fieldIdentifier.coverageLevel == question.questionField.coverageLevel &&
        fieldIdentifier.networkName == question.questionField.networkName &&
        fieldIdentifier.networkType == question.questionField.networkType &&
        fieldIdentifier.period == question.questionField.period &&
        fieldIdentifier.subject == question.questionField.subject &&
        fieldIdentifier.type == question.questionField.type
      ) {
        return true;
      }
      return false;
    });

    return !!alreadyCreatedQuestion;
  }

  openQuestionModal(fieldIdentifier?: QuestionField, id?: number) {
    this.questionModal.isLoading = true;

    if (id) {
      this.questionModal.id = id;
    }

    if (fieldIdentifier) {
      if (
        fieldIdentifier.subject !== QuestionFieldSubjectEnum.Notes &&
        this.isQuestionAlreadyCreated(fieldIdentifier)
      ) {
        this.dxcToastService.open({
          title: MessageConstants.failed,
          body: MessageConstants.questionAlreadyCreated,
          duration: AppConstants.toastDurationInMs,
          type: 'danger'
        });

        return;
      }

      if (!fieldIdentifier.networkType && fieldTypeToThrowError.includes(fieldIdentifier.type)) {
        this.dxcToastService.open({
          title: MessageConstants.failed,
          body: MessageConstants.cannotAddQuestion,
          duration: AppConstants.toastDurationInMs,
          type: 'danger'
        });

        return;
      }

      this.getDefaultQuestionByField(fieldIdentifier).subscribe((response) => {
        if (response.ok && response.body) {
          const { questionField, questions } = response.body.data;
          const field = questionField || fieldIdentifier;

          if (field.subject === QuestionFieldSubjectEnum.Notes) {
            this.questionModal.selectedQuestionId = customQuestionId;
            this.questionModal.isCustomQuestion = true;
          }

          this.questionModal.field = {
            ...field,
            originalAnswer: fieldIdentifier.originalAnswer || '-'
          };

          const onlySuggestedQuestions = (questions || []).filter(
            (question) => question.questionType === QuestionTypeEnum.Suggested
          );

          if (!onlySuggestedQuestions?.length) this.questionModal.isCustomQuestion = true;
          else if (!this.questionModal.isCustomQuestion) {
            this.questionModal.selectedQuestionId =
              onlySuggestedQuestions[0].questionId || onlySuggestedQuestions[0].id;
          }

          this.questionModal.suggestedQuestions = onlySuggestedQuestions.map((question) => ({
            ...question,
            id: question.questionId || question.id,
            answerType: AnswerTypesMap[question.answerType]
          }));

          this.questionModal.isOpen = true;
          this.questionModal.isLoading = false;
        }
      });

      return;
    }

    this.questionModal.isOpen = true;
    this.questionModal.isLoading = false;
  }

  closeQuestionModal() {
    this.questionModal.isOpen = false;
    this.questionModal.field = undefined;
    this.questionModal.selectedQuestionId = -1;
    this.questionModal.isCustomQuestion = false;
  }

  setSelectedQuestion(id: number | string) {
    this.questionModal.selectedQuestionId = id;
  }

  sendRequestToTheQueue(requestId: number) {
    return this._http.sendSecureRequest<UpdatedRequestResponse>({
      method: HttpMethod.POST,
      url: APIConstants.submitEligCompleteRequest(requestId)
    });
  }

  createNewRequest(eligibilityId: number) {
    return this._http.sendSecureRequest<EligibilityCompleteRequest>({
      method: HttpMethod.POST,
      url: APIConstants.eligibilityCompleteRequest,
      body: {
        eligibilityId,
        providerNetworkType: 'In Network',
        userId: this.authService.getUserData().user_profile.id.toString(),
        callType: 'Manual',
        priority: 'Standard'
      }
    });
  }

  updateRequestPriority(requestId: number, newPriority: EligibilityCompletePriority) {
    return this._http.sendSecureRequest<UpdatedRequestResponse>({
      method: HttpMethod.PUT,
      url: APIConstants.updateEligibilityCompletePriority(requestId),
      body: { priority: newPriority }
    });
  }

  updateRequestProviderParticipation(requestId: number, providerNetworkType: ProviderNetworkType) {
    return this._http.sendSecureRequest<UpdatedRequestResponse>({
      method: HttpMethod.PUT,
      url: APIConstants.updateEligiCompleteProviderParticipation(requestId),
      body: { providerNetworkType }
    });
  }

  getRequestsByEligibilityId({ page, limit, eligibilityId }: GetEligibilityRequestsPayload) {
    const params = new HttpParams({ fromObject: { eligibilityId, page, limit } });

    return this._http.sendSecureRequest<GetRequestsByEligibilityIdResponse>({
      method: HttpMethod.GET,
      url: APIConstants.eligibilityCompleteRequest,
      params
    });
  }

  createNewQuestion(body: CreateNewQuestionPayload) {
    return this._http.sendSecureRequest<CreateNewQuestionResponse>({
      method: HttpMethod.POST,
      url: APIConstants.addQuestionToCompleteRequestUrl(this.requestId),
      body
    });
  }

  updateQuestion(body: UpdateQuestionPayload) {
    return this._http.sendSecureRequest<UpdateQuestionResponse>({
      method: HttpMethod.PUT,
      url: APIConstants.updateQuestionToCompleteRequestUrl(this.requestId),
      body
    });
  }

  deleteQuestionById(questionId: number) {
    return this._http.sendSecureRequest<DeleteQuestionResponse>({
      method: HttpMethod.DELETE,
      url: APIConstants.deleteQuestionToCompleteRequestUrl(this.requestId),
      body: { questionId }
    });
  }

  getRequestById(id: number) {
    return this._http.sendSecureRequest<EligibilityCompleteRequest>({
      method: HttpMethod.GET,
      url: APIConstants.getCompleteRequestById(id)
    });
  }

  deleteRequestById(id: number) {
    return this._http.sendSecureRequest<EligibilityCompleteRequest>({
      method: HttpMethod.DELETE,
      url: APIConstants.deleteCompleteRequestById(id)
    });
  }

  acceptAnswer(questionId: number, requestId: number) {
    return this._http.sendSecureRequest<Question>({
      method: HttpMethod.POST,
      url: APIConstants.acceptAnswerById(requestId),
      body: { questionId }
    });
  }

  acceptAllAnswers(requestId: number) {
    return this._http.sendSecureRequest<EligibilityCompleteRequest>({
      method: HttpMethod.POST,
      url: APIConstants.acceptAllAnswers(requestId)
    });
  }

  getDefaultQuestionByField({
    type,
    category,
    code,
    subject,
    period,
    networkName,
    networkType,
    coverageLevel,
    notes
  }: FieldIdentifierPayload) {
    let params = new HttpParams({
      fromObject: {
        type,
        category: category || '',
        subject
      }
    });

    if (period) params = params.set('period', period);
    if (networkName) params = params.set('networkName', networkName);
    if (networkType) params = params.set('networkType', networkType);
    if (coverageLevel) params = params.set('coverageLevel', coverageLevel);
    if (code) params = params.set('code', code);
    if (notes) params = params.set('notes', notes);

    return this._http.sendSecureRequest<{ questions: Question[]; questionField: QuestionField }>({
      method: HttpMethod.GET,
      url: APIConstants.getDefaultQuesitonsByField,
      params
    });
  }

  getAllRequests({
    page,
    limit,
    search,
    priority,
    status,
    fromCreated,
    toCreated
  }: {
    page: number;
    limit: number;
    search?: string;
    priority?: EligibilityCompletePriority;
    status?: EligibilityCompleteRequestStatus;
    fromCreated?: string;
    toCreated?: string;
  }) {
    let params = new HttpParams({ fromObject: { page, limit } });

    params = params.set('sortBy', 'Created');
    params = params.set('orderBy', 'desc');
    if (search && search.trim().length > 0) params = params.set('search', search.trim());
    if (priority) params = params.set('priority', priority);
    if (status) params = params.set('status', status);
    if (fromCreated) params = params.set('fromCreated', fromCreated);
    if (toCreated) params = params.set('toCreated', toCreated);

    return this._http.sendSecureRequest<any>({
      method: HttpMethod.GET,
      url: APIConstants.getEligCompleteRequestsList,
      params
    });
  }
}
